import { Injectable } from '@angular/core'
import { environment } from '@employer/environments/environment'
import { RestApiClient } from '@engineering11/web-api-rest'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { DEFAULT_NETWORK_MONITORING_CONFIG, getBaseSuffix, TokenStorage } from 'shared-lib'

/**
 * Marketing Channels frontend service. Wraps the v1 REST endpoints exposed by
 * service-jobs-integration. Stories MC-13 / MC-14 / MC-15.
 *
 * Tenant scoping is implicit — the backend pulls `customerKey` from the auth
 * token via `@Claim()`. No customerKey / companyId is passed from the client.
 *
 * NO stats endpoints in v1 — those land in MC v2.
 */

export type ChannelCategory = 'Email' | 'Job Boards' | 'Social Media' | 'Professional Networks' | 'Paid Advertising'

export interface IChannelCatalogEntry {
  id: string
  name: string
  category: ChannelCategory
  brandColor: string
  brandInitials: string
  description: string
  monthlyReach: string
  // The provider adapter backing this channel; a sub-channel (e.g. `meta-ads`) shares its provider's
  // connection. `authType` mirrors the adapter: `none` = platform-managed, always available, no Connect.
  adapterId: string
  authType?: 'oauth' | 'apiKey' | 'none'
  subMode?: string
  // Distribution tier from the catalog: 'standard' is free (Default product), 'premium' requires the
  // DistributePremiumChannels entitlement (Cnect Plus). A missing tier is treated as premium (fail closed).
  tier?: 'standard' | 'premium'
  enabled: boolean
  order: number
}

export type ChannelConnectionStatus = 'connected' | 'disconnected' | 'pending'

export interface IChannelConnection {
  id: string
  customerKey: string
  channelId: string
  status: ChannelConnectionStatus
  secretKey?: string
  displayName?: string
  connectedAt?: number
  disconnectedAt?: number
  lastError?: string
}

export type ChannelIdentityType = 'company' | 'member'

export interface IChannelIdentityConnection {
  channelId: string
  identityType: ChannelIdentityType
  status: ChannelConnectionStatus
  displayName?: string
  externalId?: string
  expiresAt?: number
  connectedAt?: number
  disconnectedAt?: number
  lastError?: string
  connectedByUserId?: string
  connectedByDisplayName?: string
}

export interface IChannelIdentityView {
  channelId: string
  company?: IChannelIdentityConnection
  member?: IChannelIdentityConnection
  teammates: { connectedCount: number }
}

export interface AuthFormField {
  name: string
  label: string
  type: 'text' | 'password'
  required: boolean
}

export interface BeginConnectResult {
  authUrl?: string
  state?: string
  fields?: AuthFormField[]
}

/** A Facebook Page available for posting, with its linked Instagram professional account if any. */
export interface IMetaPage {
  pageId: string
  name: string
  avatarUrl?: string
  instagram?: { igUserId: string; username: string }
}

/**
 * A LinkedIn organization (company Page) the connector administers and can post as (CNCT-6795).
 * `pageId` is the full URN (`urn:li:organization:N`) — the same value the identities company row
 * carries as `externalId` — and is what `publishOptions.pageId` sends on an organization post.
 */
export interface ILinkedInPage {
  pageId: string
  name: string
  /** Best-effort; absent when LinkedIn will not resolve the logo. Same key as `IMetaPage`. */
  avatarUrl?: string
  /** Exactly one page is flagged while the stored default is still administered; none once it lapses. */
  isDefault: boolean
}

const BASE = 'jobs-integration/channels'

@Injectable({ providedIn: 'root' })
export class MarketingChannelsService {
  private restClient: RestApiClient

  constructor(tokenStorage: TokenStorage) {
    this.restClient = new RestApiClient({
      baseUrl: environment.services.jobsIntegration,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'jobsIntegration'),
      ...DEFAULT_NETWORK_MONITORING_CONFIG,
    })
  }

  /** Platform catalog — same content for every tenant. */
  getCatalog(): Observable<IChannelCatalogEntry[]> {
    return this.restClient.get<IChannelCatalogEntry[]>(BASE).pipe(map(item => item.data))
  }

  /** Connections for the calling user's customer (token-scoped). */
  getConnections(): Observable<IChannelConnection[]> {
    return this.restClient.get<IChannelConnection[]>(`${BASE}/connections`).pipe(map(item => item.data))
  }

  getIdentities(): Observable<IChannelIdentityView[]> {
    return this.restClient.get<IChannelIdentityView[]>(`${BASE}/connections/identities`).pipe(map(item => item.data))
  }

  refreshIdentityNames(channelId: string): Observable<IChannelIdentityView[]> {
    return this.restClient
      .post<IChannelIdentityView[]>(`${BASE}/${encodeURIComponent(channelId)}/identity-names/refresh`, {})
      .pipe(map(item => item.data))
  }

  beginConnect(channelId: string, redirectUri: string, identityType?: ChannelIdentityType, tenantOrigin?: string): Observable<BeginConnectResult> {
    const body = { redirectUri, ...(identityType ? { identityType } : {}), ...(tenantOrigin ? { tenantOrigin } : {}) }
    return this.restClient.post<BeginConnectResult>(`${BASE}/${encodeURIComponent(channelId)}/connect/begin`, body).pipe(map(item => item.data))
  }

  completeConnect(channelId: string, payload: Record<string, unknown>): Observable<IChannelConnection> {
    return this.restClient.post<IChannelConnection>(`${BASE}/${encodeURIComponent(channelId)}/connect/complete`, payload).pipe(map(item => item.data))
  }

  disconnect(channelId: string): Observable<{ status: string }> {
    return this.restClient.delete<{ status: string }>(`${BASE}/${encodeURIComponent(channelId)}`).pipe(map(item => item.data))
  }

  disconnectMember(channelId: string): Observable<{ status: string }> {
    return this.restClient.delete<{ status: string }>(`${BASE}/${encodeURIComponent(channelId)}/member`).pipe(map(item => item.data))
  }

  /**
   * Facebook Pages postable by the connected Meta account (CNCT-6738 share-post form's Page picker).
   * Not yet implemented backend-side — callers must handle failure (see backend-shape notes in
   * docs/cnct-6738-worklog.md).
   */
  getMetaPages(): Observable<IMetaPage[]> {
    return this.restClient.get<IMetaPage[]>(`${BASE}/meta/pages`).pipe(map(item => item.data))
  }

  /**
   * Company Pages the LinkedIn connection can post as (CNCT-6795). Default first, then by name; an
   * empty array is a valid answer (the connector administers no Page) and never an error. Failures
   * are the same mapping as `meta/pages` — `jobs-integration/channel-not-connected` for a dead or
   * expired connection, `jobs-integration/linkedin-rate-limited` for a LinkedIn 429.
   */
  getLinkedInPages(): Observable<ILinkedInPage[]> {
    return this.restClient.get<ILinkedInPage[]>(`${BASE}/linkedin/pages`).pipe(map(item => item.data))
  }
}
